import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint } from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';

/**
 * Stinohra (Shadow Play) - Images with shadow silhouettes
 *
 * Layout: 1 portrait page, N rows (6-20 items).
 * Each row: label image on left, shadow silhouette on right, with connection circles.
 *
 * Migrated from C# EduFrame.Img10.Generators.Stinohra
 */
export class Stinohra implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 6;
  }
  countMax(): number {
    return 20;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const pointSize = PdfUnit.FromMillimeters(5);
    const count = opts.list.length;

    const page = pdf.addPage();
    const { origin, size } = this.calcOrigin(1, count, margin, PdfSize.A4);
    const shadowMap = GUtil.genMap(opts.data.random, count);

    for (let i = 0; i < count; i++) {
      const rect = new PdfRect(origin.x, origin.y + i * size.height, size.width, size.height);
      const imgRect = new PdfRect(rect.topLeft.x, rect.topLeft.y, rect.height, rect.height);
      const shadowRect = new PdfRect(
        rect.topRight.x - rect.height,
        rect.topRight.y,
        rect.height,
        rect.height
      );

      const p1 = new PdfPoint(imgRect.right + pointSize, imgRect.centerY);
      const p2 = new PdfPoint(shadowRect.left - pointSize, shadowRect.centerY);

      const r1 = new PdfRect(p1.x - pointSize / 2, p1.y - pointSize / 2, pointSize, pointSize);
      const r2 = new PdfRect(p2.x - pointSize / 2, p2.y - pointSize / 2, pointSize, pointSize);

      // Left: label image (sequential)
      if (i < opts.list.length && opts.list[i].image) {
        page.drawImageCenter(opts.list[i].image, imgRect);
      }

      // Right: shadow image (shuffled)
      const shadowIndex = shadowMap[i];
      if (shadowIndex < opts.list.length && opts.list[shadowIndex].image) {
        const shadowImg = await opts.list[shadowIndex].image.toShadow(pdf);
        page.drawImageCenter(shadowImg, shadowRect);
      }

      // Connection circles
      page.drawEllipse(GUtil.basePen, null, r1);
      page.drawEllipse(GUtil.basePen, null, r2);
    }
  }

  private calcOrigin(
    cols: number,
    rows: number,
    margin: number,
    pageSize: PdfSize
  ): { origin: PdfPoint; size: PdfSize } {
    const w = (pageSize.width - 2 * margin) / cols;
    const h = (pageSize.height - 2 * margin) / rows;
    return {
      origin: new PdfPoint(
        margin + (pageSize.width - 2 * margin - w * cols) / 2,
        margin + (pageSize.height - 2 * margin - h * rows) / 2
      ),
      size: new PdfSize(w, h),
    };
  }
}
